import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from "react";
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
} from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { deepmerge } from "@mui/utils"; // マージ用にインポート

type Mode = "light" | "dark";

interface ThemeContextType {
  mode: Mode;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [mode, setMode] = useState<Mode>(() => {
    const isLogin = window.location.pathname === "/";
    if (isLogin) {
      localStorage.setItem("theme", "light");
      return "light";
    }
    const stored = localStorage.getItem("theme") as Mode | null;
    return stored ?? "light";
  });

  useEffect(() => {
    const stored = localStorage.getItem("theme") as Mode | null;
    if (stored) setMode(stored);
  }, []);

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === "light" ? "dark" : "light";
      localStorage.setItem("theme", next);
      return next;
    });
  };

  const theme = useMemo(() => {
    const baseThemeConfig = {
      palette: {
        mode,
        primary: { main: "#CC5500" },
        secondary: { main: mode === "dark" ? "#FF8C00" : "#A52A2A" },
        error: { main: "#8B0000" },
        warning: { main: "#FFA500" },
        success: { main: "#008000" },
        info: { main: "#0000FF" },
        background: {
          default: mode === "dark" ? "#787878" : "#FFF5F5",
          paper: mode === "dark" ? "#A9A9A9" : "#FFFFF0",
        },
        custom: {
          // カスタムパレットは最終テーマに含める必要がある
          page: mode === "dark" ? "rgb(65, 66, 66)" : "#FFD8CC",
          panel: mode === "dark" ? "#787878" : "#F19D64",
          header: mode === "dark" ? "#000000" : "#931100",
          tableHeader: mode === "dark" ? "#1e1e1e" : "#931100",
          sidebar: mode === "dark" ? "#000000" : "#F5BD97",
          row: mode === "dark" ? "#303030" : "#FFFFFF",
          mainRow: mode === "dark" ? "#DCDCDC" : "#FFFFFF",
          sForm: mode === "dark" ? "#303030" : "#F5F5DC",
          paperBorderColor: mode === "dark" ? "#3D2B1F" : "#A52A2A",
          paperBorderColorSub: mode === "dark" ? "#DCDCDC" : "#8B4513",
          underBorderColor: mode === "dark" ? "#FFD700" : "#FF8C00",
          check: mode === "dark" ? "#787878" : "#ffcccc",
          userCard: mode === "dark" ? "#2F3E46" : "#F19D64",
          userCardR: mode === "dark" ? "#4B4B4B" : "#F08080",
        },
        text: {
          primary: mode === "dark" ? "#ffffff" : "#4B0000",
        },
      },
      typography: {
        fontFamily: 'Noto Sans JP, Roboto, "Helvetica Neue", Arial, sans-serif',
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightBold: 700,
      },
      breakpoints: {
        values: {
          xs: 0,
          sm: 600,
          md: 900,
          lg: 1200,
          xl: 1536,
        },
      },
    };
    let theme = createTheme(baseThemeConfig);

    theme = deepmerge(theme, {
      typography: {
        // ... (他のタイポグラフィ設定)
        body1: {
          fontSize: "1rem",
          [theme.breakpoints.down("md")]: {
            // smサイズ (md未満)
            fontSize: "0.855rem",
          },
          [theme.breakpoints.down("sm")]: {
            // xsサイズ (sm未満)
            fontSize: "0.85rem",
          },
        },
        body2: {
          fontSize: "0.875rem",
          [theme.breakpoints.down("md")]: {
            // smサイズ (md未満)
            fontSize: "0.705rem",
          },
          [theme.breakpoints.down("sm")]: {
            // xsサイズ (sm未満)
            fontSize: "0.7rem",
          },
        },
        button: {
          fontSize: "0.875rem",
          [theme.breakpoints.down("md")]: {
            // smサイズ (md未満)
            fontSize: "0.705rem",
          },
          [theme.breakpoints.down("sm")]: {
            // xsサイズ (sm未満)
            fontSize: "0.7rem",
          },
        },
        h6: {
          fontSize: "1.25rem",
          [theme.breakpoints.down("md")]: {
            // smサイズ (md未満)
            fontSize: "1.105rem",
          },
          [theme.breakpoints.down("sm")]: {
            // xsサイズ (sm未満)
            fontSize: "1.1rem",
          },
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            ".icon": {
              width: "180px",
              height: "55px",
              marginLeft: "10px",
              cursor: "pointer",
            },
            ".status-box": {
              width: "100%",
              height: "150px",
              padding: "5px",
              fontSize: "28px",
              backgroundColor: theme.palette.background.paper,
              border: `5px solid`,
              boxSizing: "border-box",
            },
            ".alert-status-box": {
              width: "100%",
              height: "50px",
              padding: "5px",
              fontSize: "30px",
              backgroundColor: theme.palette.background.paper,
              border: `5px solid`,
              boxSizing: "border-box",
            },
            ".status-box p": { margin: "0px" },
            ".active-link": {
              backgroundColor:
                mode === "dark"
                  ? "rgba(255, 255, 255, 0.2)"
                  : "rgba(0, 0, 0, 0.08)",
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              "& fieldset": {
                borderColor: mode === "dark" ? "#ffffff" : undefined, // または theme.palette.mode
              },
              "&:hover fieldset": {
                borderColor: mode === "dark" ? "#ffffff" : undefined,
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === "dark" ? "#ffffff" : undefined,
              },
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            root: {
              color: mode === "dark" ? "#ffffff" : undefined, // または theme.palette.mode
              "&.Mui-focused": {
                color: mode === "dark" ? "#ffffff" : undefined,
              },
            },
          },
        },
      },
    });

    return theme;
  }, [mode]);

  return (
    <ThemeContext.Provider value={{ mode, toggleMode }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeMode = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeProvider");
  return ctx;
};
